import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-markdown-view.js";
import "./pd-rating-stars.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { formatShortDate } from "@progressive-development/pd-utils";
//#region src/pd-rating/PdRatingItem.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
var PdRatingItem = class PdRatingItem extends LitElement {
	constructor(..._args) {
		super(..._args);
		this._expanded = false;
		this._overflowing = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;
      container-type: inline-size;
      --pd-rating-star-size: 1rem;
    }

    .row {
      padding: 0.6rem 0;
    }

    .title-line {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .title {
      font-weight: 600;
      color: var(--pd-default-font-title-col, inherit);
    }

    time,
    .source-line {
      font-size: var(--pd-rating-font-size-sub, 0.85em);
      color: var(--pd-default-font-muted-col, #757575);
    }

    time {
      white-space: nowrap;
    }

    .stars-line {
      margin-top: 0.15rem;
    }

    .text {
      margin-top: 0.35rem;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: var(--pd-rating-text-clamp-lines, 6);
      overflow: hidden;
    }

    .text.expanded {
      display: block;
      overflow: visible;
    }

    button.toggle {
      font: inherit;
      border: none;
      background: none;
      padding: 0.25rem 0;
      margin: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      cursor: pointer;
      color: var(--pd-default-font-link-col, inherit);
      font-size: var(--pd-rating-font-size-sub, 0.85em);
      min-height: 2rem;
    }

    button.toggle:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col, currentColor);
      outline-offset: 2px;
    }

    .chevron {
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(90deg);
    }

    .source-line {
      margin-top: 0.25rem;
    }

    @container (max-width: 480px) {
      .title-line time {
        flex-basis: 100%;
      }
    }
  `;
	}
	render() {
		const entry = this.entry;
		if (!entry) return nothing;
		return html`
      <div class="row">
        <div class="title-line">
          <span class="title">${entry.actorLabel}</span>
          <time datetime="${entry.at}" title="${entry.at}">
            ${formatShortDate(entry.at, false)}
          </time>
        </div>
        ${entry.rating ? html`
                <div class="stars-line">
                  <pd-rating-stars .value="${entry.rating}"></pd-rating-stars>
                </div>
              ` : nothing}
        ${entry.text ? html`
                <div
                  class="text ${this._expanded ? "expanded" : ""}"
                  id="textId"
                >
                  <pd-markdown-view
                    .markdown="${entry.text}"
                  ></pd-markdown-view>
                </div>
              ` : nothing}
        ${this._overflowing || this._expanded ? html`
                <button
                  class="toggle"
                  aria-expanded="${this._expanded}"
                  aria-controls="textId"
                  @click="${() => this._expanded = !this._expanded}"
                >
                  <span
                    class="chevron ${this._expanded ? "open" : ""}"
                    aria-hidden="true"
                    >▸</span
                  >
                  ${this._expanded ? msg("Weniger anzeigen", { id: "pd.rating.item.showLess" }) : msg("Mehr anzeigen", { id: "pd.rating.item.showMore" })}
                </button>
              ` : nothing}
        ${entry.source ? html`
                <div class="source-line">
                  ${msg("Quelle", { id: "pd.rating.item.source" })}:
                  ${entry.source}
                </div>
              ` : nothing}
      </div>
    `;
	}
	updated(changedProps) {
		if (changedProps.has("entry")) {
			this._expanded = false;
			this._measureClamp();
		}
	}
	/** The clamp is visual (line-clamp) — whether the toggle is needed can
	*  only be measured after the markdown view has rendered. */
	async _measureClamp() {
		await this.renderRoot.querySelector("pd-markdown-view")?.updateComplete;
		requestAnimationFrame(() => {
			const el = this._textEl;
			this._overflowing = !!el && el.scrollHeight > el.clientHeight + 1;
		});
	}
};
__decorate([property({ attribute: false })], PdRatingItem.prototype, "entry", void 0);
__decorate([state()], PdRatingItem.prototype, "_expanded", void 0);
__decorate([state()], PdRatingItem.prototype, "_overflowing", void 0);
__decorate([query(".text")], PdRatingItem.prototype, "_textEl", void 0);
PdRatingItem = __decorate([localized()], PdRatingItem);
//#endregion
export { PdRatingItem };
